import { Component } from '@theme/component';

const ENDPOINT = '/apps/htc-collector/contact-attachments';
const SOURCE_MAX = 20 * 1024 * 1024;
const PHOTO_MAX = 450000;
const TYPES = /^(image\/(jpeg|png|webp|heic|heif))$/i;

/** Photo uploads finish before Shopify handles the ordinary contact submission. */
class HTCContactAttachments extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  setup() {
    const form = this.closest('form');
    const fields = [...this.querySelectorAll('[data-photo-side]')];
    if (!form || fields.length !== 2) return;
    if (this.form === form && this.slots?.every((slot, i) => slot.field === fields[i] && slot.input === fields[i].querySelector('[data-photo-file]') && slot.url === fields[i].querySelector('[data-photo-url]'))) return;
    this.teardown();
    this.form = form;
    this.listeners = new AbortController();
    this.submitButtons = [...form.querySelectorAll('button[type="submit"],input[type="submit"]')];
    this.disabledButtons = new Set();
    this.redemption = this.querySelector('[data-redemption]');
    this.slots = fields.map((field) => ({
      field,
      side: field.dataset.photoSide,
      input: field.querySelector('[data-photo-file]'),
      url: field.querySelector('[data-photo-url]'),
      status: field.querySelector('[data-photo-status]'),
      remove: field.querySelector('[data-photo-remove]'),
      generation: 0,
      busy: false,
      error: '',
      expiresAt: 0,
    }));
    for (const slot of this.slots) {
      // Browsers may restore a file selection without restoring its upload result.
      slot.url.value = '';
      slot.url.disabled = true;
      slot.input.value = '';
      slot.input.addEventListener('change', () => this.upload(slot), { signal: this.listeners.signal });
      slot.remove.addEventListener('click', () => this.clear(slot), { signal: this.listeners.signal });
    }
    if (new URL(location.href).searchParams.get('topic') === 'golden-ticket') this.redemption.checked = true;
    this.redemption.addEventListener('change', () => this.refresh(), { signal: this.listeners.signal });
    form.addEventListener('submit', (event) => this.guardSubmission(event), {
      capture: true,
      signal: this.listeners.signal,
    });
    this.hidden = false;
    const fallback = form.querySelector('[data-contact-photos-fallback]');
    if (fallback) fallback.hidden = true;
    this.refresh();
  }

  teardown() {
    this.listeners?.abort();
    for (const slot of this.slots || []) {
      slot.generation++;
      slot.controller?.abort();
    }
    for (const button of this.disabledButtons || []) button.disabled = false;
    this.form?.removeAttribute('aria-busy');
    this.form = null;
  }

  message(key) {
    return this.dataset[`${key}Message`] || this.dataset.uploadErrorMessage;
  }

  clear(slot) {
    slot.generation++;
    slot.controller?.abort();
    slot.input.value = '';
    slot.input.removeAttribute('aria-invalid');
    slot.url.value = '';
    slot.url.disabled = true;
    slot.status.textContent = '';
    slot.remove.hidden = true;
    slot.busy = false;
    slot.error = '';
    slot.expiresAt = 0;
    this.refresh();
  }

  refresh() {
    const busy = this.slots.some((slot) => slot.busy);
    this.form.setAttribute('aria-busy', String(busy));
    for (const button of this.submitButtons) {
      if (busy && !button.disabled) {
        this.disabledButtons.add(button);
        button.disabled = true;
      } else if (!busy && this.disabledButtons.has(button)) {
        button.disabled = false;
        this.disabledButtons.delete(button);
      }
    }
    for (const slot of this.slots) {
      const missing = this.redemption.checked && !slot.url.value;
      slot.input.setAttribute('aria-required', String(this.redemption.checked));
      slot.input.setCustomValidity(slot.busy ? this.message('uploading') : slot.error || (missing ? this.message('required') : ''));
    }
  }

  guardSubmission(event) {
    for (const slot of this.slots) {
      if (slot.url.value && slot.expiresAt <= Date.now()) {
        slot.url.value = '';
        slot.url.disabled = true;
        slot.error = this.message('expired');
        slot.status.textContent = slot.error;
      }
    }
    this.refresh();
    const invalid = this.slots.find((slot) => slot.busy || slot.error || (this.redemption.checked && !slot.url.value));
    if (!invalid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    invalid.input.focus();
    invalid.input.reportValidity();
  }

  async upload(slot) {
    const file = slot.input.files?.[0];
    slot.generation++;
    const generation = slot.generation;
    slot.controller?.abort();
    slot.controller = new AbortController();
    slot.url.value = '';
    slot.url.disabled = true;
    slot.expiresAt = 0;
    slot.error = '';
    slot.busy = Boolean(file);
    slot.remove.hidden = !file;
    slot.input.removeAttribute('aria-invalid');
    slot.status.textContent = file ? this.message('uploading') : '';
    this.refresh();
    if (!file) return;
    try {
      if (file.size > SOURCE_MAX || !file.size) throw new Error(this.message('sizeError'));
      if (!TYPES.test(file.type) && !(!file.type && /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name))) {
        throw new Error(this.message('typeError'));
      }
      const data = await this.preparePhoto(file, slot.controller.signal);
      if (generation !== slot.generation || !this.isConnected) return;
      const session = await this.request(`${ENDPOINT}/session`, { method: 'GET' }, slot.controller.signal);
      if (typeof session.token !== 'string' || !session.token) throw new Error(this.message('uploadError'));
      const result = await this.request(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, photos: [{ side: slot.side, data }] }),
      }, slot.controller.signal);
      if (generation !== slot.generation || !this.isConnected) return;
      const attachment = result.attachments?.find((item) => item.side === slot.side);
      const url = new URL(attachment?.url || 'invalid', location.origin);
      if (url.origin !== 'https://hollywoodtradingcards.com' || !/^\/apps\/htc-collector\/contact-attachments\/[a-f0-9]{64}$/.test(url.pathname) || !/^[a-f0-9]{64}$/.test(url.searchParams.get('key') || '') || url.username || url.password || url.hash || !Number.isFinite(result.expiresAt) || result.expiresAt <= Date.now()) {
        throw new Error(this.message('uploadError'));
      }
      slot.url.value = url.href;
      slot.url.disabled = false;
      slot.expiresAt = result.expiresAt;
      slot.status.textContent = this.message('ready');
    } catch (error) {
      if (generation !== slot.generation || !this.isConnected) return;
      slot.error = error.name === 'AbortError' ? this.message('uploadError') : error.message || this.message('uploadError');
      slot.status.textContent = slot.error;
      slot.input.value = '';
      slot.input.setAttribute('aria-invalid', 'true');
    } finally {
      if (generation === slot.generation && this.isConnected) {
        slot.busy = false;
        this.refresh();
      }
    }
  }

  async request(url, options, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 30000);
    try {
      if (signal.aborted) controller.abort();
      const response = await fetch(url, { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.error === 'string' && result.error.length < 250 ? result.error : this.message('uploadError'));
      return result;
    } catch (error) {
      if (error instanceof SyntaxError || error instanceof TypeError) throw new Error(this.message('uploadError'));
      throw error;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    }
  }

  async preparePhoto(file, signal) {
    const source = URL.createObjectURL(file);
    const image = new Image();
    let timer;
    let abort;
    try {
      await new Promise((resolve, reject) => {
        abort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
        signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => reject(new Error(this.message('decodeError'))), 15000);
        image.onload = resolve;
        image.onerror = () => reject(new Error(this.message('decodeError')));
        if (signal.aborted) return abort();
        image.src = source;
      });
      if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 60000000) {
        throw new Error(this.message('decodeError'));
      }
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) throw new Error(this.message('decodeError'));
      for (const maxEdge of [1600, 1280, 1024]) {
        const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        for (const quality of [0.9, 0.8, 0.7, 0.6]) {
          if (signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
          const data = canvas.toDataURL('image/jpeg', quality);
          const payload = data.slice(data.indexOf(',') + 1);
          const bytes = payload.length * 3 / 4 - (payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0);
          if (data.startsWith('data:image/jpeg;base64,') && bytes <= PHOTO_MAX) return data;
        }
      }
      throw new Error(this.message('decodeError'));
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      image.onload = image.onerror = null;
      URL.revokeObjectURL(source);
    }
  }
}

if (!customElements.get('htc-contact-attachments')) customElements.define('htc-contact-attachments', HTCContactAttachments);
